/**
 * Unit-of-measure TOKEN helpers — the frontend twin of `shared/utils/uom.py`.
 *
 * Every `uom` column across the ERP cluster stores a token from the global
 * catalog (`shared/config/uom.config.json` → generated `UOM_VALUES`); the UI
 * shows the translated label (`common:uom.<token>`) and never the raw token.
 */
import type { TFunction } from 'i18next';
import { UOM_VALUES, type Uom } from '../../generated/config-enums/uom';

const UOM_SET: ReadonlySet<string> = new Set(UOM_VALUES);

/** Whether `value` is a catalog token (never free text). */
export function isUomToken(value: string | null | undefined): value is Uom {
  return value != null && UOM_SET.has(value);
}

/**
 * The label a read surface shows for a stored `uom`: the translated catalog
 * label for a token. A value that is NOT a token is a row written before the
 * catalog existed (the DB CHECKs land in Stage 4) — it is shown as the word
 * it is, since inventing a unit for it would be worse than showing what the
 * document said. An empty/null value renders as nothing.
 */
export function uomLabel(t: TFunction, value: string | null | undefined): string {
  if (!value) return '';
  return isUomToken(value) ? t(`common:uom.${value}`) : value;
}
